import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Card, CardActionButton } from "../components/Card";
import { Chip } from "../components/Chip";
import { TruncatedText } from "../components/TruncatedText";
import { Minus, Plus, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
const FADE_TRANSITION_CLASSES = "transition-opacity ease-in-out";
export const ACTION_CARD_DIFF_STATUSES = ["added", "removed"];
const actionCardDiffVariants = cva("p-3", {
  variants: {
    diffStatus: {
      added: cn("bg-success-50 border-success-200"),
      removed: cn("bg-warning-50 border-warning-200")
    }
  }
});
const DIFF_CHIP_CONFIG = {
  added: {
    color: "success",
    icon: Plus
  },
  removed: {
    color: "warning",
    icon: Minus
  }
};

// Selection mode <-> card in a picker grid.

// Display mode <-> card already added.

// Display mode with diff indicator.

/**
 * A card representing a capability or tool in the agent builder, showing an icon,
 * label, and description with an optional footer link. It has three modes: selectable
 * (`canAdd` + `isSelected`) for toggling a tool on an agent, display-only
 * (`canAdd={false}`), and diff (`diffStatus` of `added` / `removed`) for pending changes.
 * Use it to list an agent's tools and let builders add or remove them, or to preview
 * toolset changes. For an in-conversation accept/reject action proposal, use
 * ActionCardBlock instead.
 * @summary Tool card for the agent builder.
 */
export const ActionCard = React.forwardRef(({
  icon,
  iconBackgroundColor,
  iconColor,
  label,
  description,
  isSelected,
  canAdd,
  cantAddReason,
  disabled,
  diffStatus,
  footer,
  onRemove,
  onClick,
  className,
  cardContainerClassName,
  mountPortal,
  mountPortalContainer,
  descriptionLineClamp = 2
}, ref) => {
  const {
    _: _t
  } = _useLingui();
  const diffChip = diffStatus ? DIFF_CHIP_CONFIG[diffStatus] : null;
  return /*#__PURE__*/React.createElement(Card, {
    ref: ref,
    variant: isSelected ? "secondary" : "primary",
    onClick: onClick,
    disabled: disabled !== null && disabled !== void 0 ? disabled : !canAdd,
    containerClassName: cardContainerClassName,
    className: cn(actionCardDiffVariants({
      diffStatus
    }), className),
    action: onRemove ? /*#__PURE__*/React.createElement(CardActionButton, {
      size: "icon",
      icon: XClose,
      tooltip: _t(
      /** i18n */
      {
        id: "t_YqKh",
        message: "Remove"
      }),
      onClick: e => {
        onRemove();
        e.stopPropagation();
      }
    }) : undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex h-full w-full flex-col justify-between"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mb-2 flex flex-wrap items-center justify-between gap-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex min-w-0 items-center gap-2"
  }, /*#__PURE__*/React.createElement(Avatar, {
    icon: icon,
    size: "xs",
    backgroundColor: iconBackgroundColor,
    iconColor: iconColor
  }), /*#__PURE__*/React.createElement("span", {
    className: "truncate text-sm font-medium"
  }, label), isSelected && /*#__PURE__*/React.createElement(Chip, {
    size: "mini",
    color: "success",
    label: _t(
    /** i18n */
    {
      id: "hp8OtS",
      message: "Added"
    }),
    className: cn(FADE_TRANSITION_CLASSES, "opacity-100")
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-shrink-0 items-center gap-2"
  }, diffChip && /*#__PURE__*/React.createElement(Chip, {
    size: "xs",
    color: diffChip.color,
    icon: diffChip.icon,
    className: cn(FADE_TRANSITION_CLASSES, "opacity-100")
  }), canAdd && /*#__PURE__*/React.createElement(Button, {
    size: "xs",
    variant: "outline",
    icon: Plus,
    label: _t(
    /** i18n */
    {
      id: "m16xKo",
      message: "Add"
    }),
    className: cn(FADE_TRANSITION_CLASSES, "flex-shrink-0")
  }), cantAddReason && /*#__PURE__*/React.createElement("div", {
    className: "flex-shrink-0 text-xs italic text-muted-foreground"
  }, cantAddReason))), /*#__PURE__*/React.createElement(TruncatedText, {
    className: "text-xs text-muted-foreground",
    mountPortal: mountPortal,
    mountPortalContainer: mountPortalContainer,
    lineClamp: descriptionLineClamp
  }, description)), footer && /*#__PURE__*/React.createElement("div", null, footer.onClick ? /*#__PURE__*/React.createElement("a", {
    onClick: e => {
      var _footer$onClick;
      e.stopPropagation();
      (_footer$onClick = footer.onClick) === null || _footer$onClick === void 0 || _footer$onClick.call(footer);
    },
    className: "text-xs cursor-pointer text-muted-foreground hover:text-highlight-light hover:underline hover:underline-offset-2"
  }, footer.label) : /*#__PURE__*/React.createElement("span", {
    className: "text-xs text-muted-foreground"
  }, footer.label))));
});
ActionCard.displayName = "ActionCard";
//# sourceMappingURL=ActionCard.js.map